import React, { createContext, useContext, useMemo } from "react";
import { useMenuFromApi } from "../hooks/useMenuFromApi";
import type { TocMenuItem } from "../types/menu";

/**
 * Label info for an ItemType from TOC menu
 */
interface ItemTypeLabel {
  label?: string;
  labelVi?: string;
  labelEn?: string;
}

/**
 * Context value containing TOC label lookups
 */
interface TocLabelContextValue {
  /** Map of itemTypeName -> label info */
  labelsByItemType: Map<string, ItemTypeLabel>;
  /** Whether labels are still loading */
  isLoading: boolean;
  /** Get localized label for an ItemType */
  getLabel: (itemTypeName: string, language: string) => string | undefined;
}

const TocLabelContext = createContext<TocLabelContextValue | null>(null);

/**
 * Flatten TOC menu items to extract all ItemType labels
 */
function extractLabels(items: TocMenuItem[]): Map<string, ItemTypeLabel> {
  const labels = new Map<string, ItemTypeLabel>();

  function traverse(menuItems: TocMenuItem[]) {
    for (const item of menuItems) {
      if (item.itemTypeName && item.action === "ItemType") {
        labels.set(item.itemTypeName, {
          label: item.label,
          labelVi: item.labelVi,
          labelEn: item.labelEn,
        });
      }
      if (item.children && item.children.length > 0) {
        traverse(item.children);
      }
    }
  }

  traverse(items);
  return labels;
}

/**
 * Provider component that loads TOC labels and makes them available
 */
export const TocLabelProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { allMenuItems, isLoading } = useMenuFromApi();

  const labelsByItemType = useMemo(() => {
    if (!allMenuItems || allMenuItems.length === 0) {
      return new Map<string, ItemTypeLabel>();
    }
    return extractLabels(allMenuItems);
  }, [allMenuItems]);

  const getLabel = useMemo(() => {
    return (itemTypeName: string, language: string): string | undefined => {
      const labelInfo = labelsByItemType.get(itemTypeName);
      if (!labelInfo) return undefined;

      if (language === "vi" && labelInfo.labelVi) {
        return labelInfo.labelVi;
      }
      if (language === "en" && labelInfo.labelEn) {
        return labelInfo.labelEn;
      }
      return labelInfo.label;
    };
  }, [labelsByItemType]);

  const value = useMemo(
    () => ({
      labelsByItemType,
      isLoading,
      getLabel,
    }),
    [labelsByItemType, isLoading, getLabel],
  );

  return (
    <TocLabelContext.Provider value={value}>
      {children}
    </TocLabelContext.Provider>
  );
};

/**
 * Hook to access TOC label context
 */
export function useTocLabels(): TocLabelContextValue {
  const context = useContext(TocLabelContext);
  if (!context) {
    // Return a default value if used outside provider (graceful fallback)
    return {
      labelsByItemType: new Map(),
      isLoading: false,
      getLabel: () => undefined,
    };
  }
  return context;
}
